import { Field } from "@base-ui/react/field";
import type { ComponentProps } from "react";
import { cx } from "../../lib/cx";
import { FIELD_INPUT, FieldFrame } from "./field-frame";

/**
 * A labelled text input and a labelled text area, on Base UI's Field.
 *
 * Controlled (plan D09): `value` and `onValueChange`. The caller validates
 * with the contract schema and hands back `error` as text; the field paints
 * it and links it to the control.
 */

type InputAttributes = Pick<
  ComponentProps<"input">,
  | "autoComplete"
  | "autoCapitalize"
  | "enterKeyHint"
  | "inputMode"
  | "maxLength"
  | "name"
  | "placeholder"
  | "type"
>;

export interface TextFieldProps extends InputAttributes {
  className?: string;
  description?: string;
  disabled?: boolean;
  error?: string;
  label: string;
  onValueChange: (value: string) => void;
  value: string;
}

export function TextField({
  value,
  onValueChange,
  label,
  description,
  error,
  disabled,
  className,
  ...input
}: TextFieldProps) {
  return (
    <FieldFrame
      className={className}
      description={description}
      disabled={disabled}
      error={error}
      label={label}
    >
      <Field.Control
        className={cx(FIELD_INPUT, "h-12")}
        onValueChange={onValueChange}
        value={value}
        {...input}
      />
    </FieldFrame>
  );
}

export interface TextAreaProps {
  className?: string;
  description?: string;
  disabled?: boolean;
  error?: string;
  label: string;
  maxLength?: number;
  onValueChange: (value: string) => void;
  placeholder?: string;
  /** Visible lines before it scrolls. */
  rows?: number;
  value: string;
}

export function TextArea({
  value,
  onValueChange,
  label,
  description,
  error,
  placeholder,
  maxLength,
  rows,
  disabled,
  className,
}: TextAreaProps) {
  return (
    <FieldFrame
      className={className}
      description={description}
      disabled={disabled}
      error={error}
      label={label}
    >
      <Field.Control
        className={cx(FIELD_INPUT, "resize-none py-3 leading-snug")}
        maxLength={maxLength}
        onValueChange={onValueChange}
        placeholder={placeholder}
        render={<textarea rows={rows ?? 4} />}
        value={value}
      />
    </FieldFrame>
  );
}
